import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { InviteComponent } from '../invite/invite.component';
import { UtilsService } from 'src/app/services/utils.service';
import { CandidateService } from 'src/app/services/candidate.service';
import { setSelectedCandidates } from 'src/app/actions/selected-candidates.actions';
import { Candidate, CandidateCreate } from 'src/app/models/candidate.model';
import { Observable, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { Consultant } from 'src/app/models/consultant.model';
import { fromConsultant } from 'src/app/reducers/consultant.reducer';
import { CustomValidators } from 'src/app/helpers/custom-validators';
import { AppConfig } from 'src/app/helpers/app.config';
import { ModalDismissReasons, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'ct-upload-candidates',
  templateUrl: './upload-candidates.component.html',
  styleUrls: ['./upload-candidates.component.scss']
})
export class UploadCandidatesComponent implements OnInit, OnDestroy {

  @ViewChild(InviteComponent) invite;

  @ViewChild('content') content;
  @ViewChild('loadInstructionsModal') loadInstructionsModal;

  modalRef: NgbModalRef;
  closeResult: string;

  consultant$: Observable<Consultant>;
  consultantSubscription: Subscription;

  candidates: CandidateCreate[];
  fileName = 'Seleccionar archivo';
  page = 1;
  pageSize = 10;
  filterErrors = false;

  allCandidates: [Candidate[]] = [[]];
  selectedCandidates: any;
  totalCandidates: number;
  responseTimes: number[] = [];
  errorEmails: string[] = [];
  public isCollapsed = true;

  step: 'uploadCandidates' | 'reviewCandidates' | 'candidatesUploadProgress' | 'candidatesUploadResume' | 'uploadTests' = 'uploadCandidates';

  constructor(
    private store: Store,
    public router: Router,
    public utilsService: UtilsService,
    private candidateService: CandidateService,
    private modal: NgbModal,
  ) { }

  ngOnInit(): void {
    this.consultant$ = this.store.pipe(select(fromConsultant.selectConsultantState));
  }

  @HostListener('window:beforeunload', ['$event'])
  onTabClose(event) {
    if (this.step !== 'uploadCandidates') {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  onFileSelect(input: HTMLInputElement) {
    const files = input.files;
    if (files && files.length && files[0].name.slice(-3) === 'csv') {
      this.fileName = files[0].name;
      const fileReader = new FileReader();

      fileReader.addEventListener('load', (fileLoadedEvent) => {
        const txt = fileLoadedEvent.target.result as string;
        const csv = [];
        const lines = txt.split('\n');

        for (const element of lines) {
          const cols: string[] = element.split(';');
          if (cols[3] === 'MailAddress' || cols[3] === 'email' || cols.length === 1) {
            continue;
          }

          const candidate = new CandidateCreate();
          candidate.name = cols[0] ?? '';
          candidate.surname1 = cols[1] ?? '';
          candidate.surname2 = cols[2] ?? '';
          candidate.email = cols[3] ?? '';
          candidate.idCard = cols[4] ?? '';
          candidate.checked = true;

          this.checkCandidateValidity(candidate, cols);

          csv.push(candidate);
        }

        this.candidates = csv;
        this.step = 'reviewCandidates';
      });

      fileReader.readAsText(files[0], 'UTF-8');
    } else {
      this.utilsService.toast('warning', 'WrongCsvFormat');
    }
  }

  toggleCandidate(candidate) {
    if (!candidate.hasError) {
      candidate.checked = !candidate.checked;
    }
  }

  toggleAllCandidates(event) {
    for (const candidate of this.candidates) {
      if (!candidate.hasError && !candidate.hasWarning) {
        candidate.checked = event.target.checked;
      } else {
        if (event.target.checked === false) {
          candidate.checked = false;
        }
      }
    }
  }

  getCheckedCandidates() {
    return this.candidates.filter(candidate => candidate.checked);
  }

  upload() {
    const size = (AppConfig.settings.SIZE_ARRAY_IN_GROUP !== undefined) ? (AppConfig.settings.SIZE_ARRAY_IN_GROUP * 1) : 3;
    const elements = this.utilsService.arrayInGroups(this.getCheckedCandidates(), size);
    // Guardamos la oficina
    this.consultantSubscription = this.consultant$.pipe(take(1)).subscribe(consultant => {
      this.allCandidates = [[]];
      this.allCandidates.pop();
      this.selectedCandidates = [];
      this.selectedCandidates.pop();
      this.totalCandidates = this.getCheckedCandidates().length;
      this.step = 'candidatesUploadProgress';
      this.invite.open(true);

      this.setCreateAsSequence(elements, consultant.office.id, 0, consultant.client.id);
    });
  }

  // Funcion recursiva
  setCreateAsSequence(elements: any[], officeId: number, index: number, clientId: number) {
    const requestStartDate = Date.now();

    this.candidateService.create(elements[index], officeId, clientId).pipe(
      finalize(() => {
        if (index < elements.length - 1) {
          this.setCreateAsSequence(elements, officeId, (index + 1), clientId);
        }
      })
    )
      .subscribe(candidates => {
        // Esperamos a que nos devuelve valores.
        if (candidates !== null || candidates !== undefined) {
          // guardamos los elementos que devuelve
          // Cuando solo algunos de los candidatos del lote dan error los devuelve con id 0, lo usamos para identificarlo
          this.allCandidates.push(candidates.filter(candidate => candidate.id !== 0));
          this.responseTimes.push(Math.round((Date.now() - requestStartDate) / 1000));
        }

        this.errorEmails.push(...candidates.filter(candidate => candidate.id === 0).map(candidate => candidate.email));

      }, error => {
        const candidatesWithErrors = JSON.parse(error.message).error;
        this.errorEmails.push(...candidatesWithErrors);
      }).add(() => {
        const candidatesCreatedLength = this.allCandidates.flat().length;
        // comparamos los dos arrays para ver si tienen el mismo número de elementos
        if (elements.flat().length === (candidatesCreatedLength + this.errorEmails.length)) {
          for (const ct of this.allCandidates) {
            // recorremos y nos quedamos con los candis
            this.selectedCandidates.push(...ct);
          }

          this.store.dispatch(setSelectedCandidates({ selectedCandidates: this.selectedCandidates }));
          setTimeout(() => {
            this.step = 'candidatesUploadResume';
          }, 1000);
        }
      });
  }

  showModalEditInstructions() {
    if (!this.modal.hasOpenModals()) {
      this.modalRef = this.modal.open(this.content, { ariaLabelledBy: 'como-editar-csv', size: 'l', backdrop: 'static' });
      this.modalRef.result.then((result) => {
        this.closeResult = 'Closed with: ' + result;
      }, (reason) => {
        this.closeResult = 'Dismissed ' + this.getDismissReason(reason);
      });
    }
  }

  showModalLoadInstructions() {
    if (!this.modal.hasOpenModals()) {
      this.modalRef = this.modal.open(this.loadInstructionsModal, {
        ariaLabelledBy: 'como-cargar-csv', size: 'xl', scrollable: true, backdrop: 'static'
      });
      this.modalRef.result.then((result) => {
        this.closeResult = 'Closed with: ' + result;
      }, (reason) => {
        this.closeResult = 'Dismissed ' + this.getDismissReason(reason);
      });
    }
  }

  getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return 'with: ' + reason;
    }
  }

  resetFile() {
    this.candidates = null;
    this.fileName = 'Seleccionar archivo';
    this.step = 'uploadCandidates';
  }

  errorCount(): number {
    return this.candidates.filter(candidate => candidate.hasError === true || candidate.hasWarning === true).length;
  }

  checkCandidateValidity(candidate: CandidateCreate, cols?: string[]) {
    // Hacer trim a las propiedades del objeto antes de evaluar
    Object.keys(candidate).map(
      key => candidate[key] = typeof candidate[key] === 'string' ? candidate[key].replace(/\s+/g, ' ').trim() : candidate[key]
    );

    if (
      (cols && cols.length !== 5) ||
      candidate.name === '' ||
      candidate.surname1 === '' ||
      candidate.email === '' ||
      !candidate.email.match(CustomValidators.emailRegex)
    ) {
      // Si se cumplen las condiciones se marca la fila con error
      candidate.hasError = true;
      candidate.checked = false;
    }
    else if (
      candidate.idCard.includes(' ') ||
      !candidate.name.match(CustomValidators.alphaRegex) ||
      !candidate.surname1.match(CustomValidators.alphaRegex) ||
      !candidate.surname2.match(CustomValidators.alphaRegex)
    ) {
      // Si se cumplen las condiciones se marca la fila con advertencia
      candidate.hasError = false;
      candidate.hasWarning = true;
      candidate.checked = false;
    }
    else {
      candidate.hasError = false;
      candidate.hasWarning = false;
    }

    // Si se esta entrando a la funcion por un cambio en el input y no hay errores eliminar filtro de errores
    if (!cols && this.errorCount() === 0) {
      this.filterErrors = false;
    }
  }

  candidatesFiltered(): CandidateCreate[] {
    if (this.filterErrors) {
      return this.candidates.filter(candidate => candidate.hasError === true || candidate.hasWarning === true);
    } else {
      return this.candidates;
    }
  }

  exportErrors(emails?: string[]) {
    const headers = ['name', 'surname1', 'surname2', 'email', 'idCard'];
    this.utilsService.exportToCsv(
      this.candidates.filter(candidate => emails ? emails.includes(candidate.email) : (candidate.hasWarning || candidate.hasError)),
      'candidates-errors', headers
    );
  }

  uploadProgress(): number {
    return (this.allCandidates.flat().length + this.errorEmails.length) * 100 / this.totalCandidates;
  }

  estimatedTime(): string {
    if (!this.responseTimes.length) {
      return this.utilsService.secondsToTime((this.totalCandidates - this.allCandidates.flat().length) * 5);
    } else {
      // El tiempo estimado sera igual al promedio de los tiempos de respuesta multiplicado por el numero de llamadas restante
      return this.utilsService.secondsToTime(
        (this.totalCandidates - this.allCandidates.flat().length) *
        (
          this.responseTimes.reduce((total, current) => total + current, 0) /
          (this.responseTimes.length * (AppConfig.settings.SIZE_ARRAY_IN_GROUP || 3))
        )
      );
    }
  }

  ngOnDestroy(): void {
    if (this.consultantSubscription) {
      this.consultantSubscription.unsubscribe();
    }
  }

}
